import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface PreviousBuyingProduct {
  product_id: number;
  productName: string;
  image_path: string;
  barCode?: string;
  unit_price?: number;
  latest_order_id?: number;
  lastPurchasedAt?: string;
  link?: string;
}

interface PreviousBuyingContextType {
  products: PreviousBuyingProduct[];
  loading: boolean;
  error: string | null;
  refetchPreviousBuyingProducts: () => Promise<void>;
  getProductImage: (productId?: number | string, image?: string) => string;
}

const PreviousBuyingContext = createContext<
  PreviousBuyingContextType | undefined
>(undefined);

export function PreviousBuyingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const [products, setProducts] = useState<PreviousBuyingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getProductImage = useCallback(
    (productId?: number | string, image?: string) => {
      if (!productId || !image) return "";
      return `${IMAGE_URL}/uploads/products/${productId}/${image}`;
    },
    [IMAGE_URL],
  );

  const refetchPreviousBuyingProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${BASE_URL}/previous-buying?limit=10`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result?.success) {
        const normalized = (result.data || []).map((item: any) => ({
          product_id: item.product_id,
          productName: item.productName || "",
          image_path: item.image_path || "",
          barCode: item.barCode || item.bar_code || "",
          unit_price: Number(item.unit_price || 0),
          latest_order_id: item.latest_order_id,
          lastPurchasedAt: item.lastPurchasedAt,
          link:
            item.barCode || item.bar_code
              ? `/single-product/${item.barCode || item.bar_code}`
              : "#",
        }));

        setProducts(normalized);
      } else {
        setProducts([]);
        setError(result?.message || "Unable to fetch previous buying products.");
      }
    } catch (err) {
      console.error("Previous buying fetch error:", err);
      setProducts([]);
      setError("Something went wrong while fetching previous buying products.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    refetchPreviousBuyingProducts();
  }, [refetchPreviousBuyingProducts]);

  const value = useMemo(
    () => ({
      products,
      loading,
      error,
      refetchPreviousBuyingProducts,
      getProductImage,
    }),
    [products, loading, error, refetchPreviousBuyingProducts, getProductImage],
  );

  return (
    <PreviousBuyingContext.Provider value={value}>
      {children}
    </PreviousBuyingContext.Provider>
  );
}

export function usePreviousBuyingProducts() {
  const context = useContext(PreviousBuyingContext);

  if (!context) {
    throw new Error(
      "usePreviousBuyingProducts must be used within a PreviousBuyingProvider",
    );
  }

  return context;
}